import { AlignBoxMiddleCenter16 } from '@carbon/icons-react';
import Carbon16 from '@carbon/icons-react/es/carbon/16';
import Chat16 from '@carbon/icons-react/es/chat/16';
import EventSchedule16 from '@carbon/icons-react/es/event--schedule/16';
import Flag16 from '@carbon/icons-react/es/flag/16';
import Information20 from '@carbon/icons-react/es/information/20';
import ListChecked16 from '@carbon/icons-react/es/list--checked/16';
import Task16 from '@carbon/icons-react/es/task/16';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ReactComponent as SlackLogo } from 'assets/svgs/slack-logo.svg';
import { Approved16 } from 'components/atoms/icons';
import { TaskSettings16 } from 'components/atoms/icons/task-settings';
import { InlineNotification } from 'components/atoms/inline-notification';
import { InlineNotificationType } from 'components/atoms/inline-notification/inline-notification-types';
import { MaskableContent } from 'components/atoms/maskable-content';
import {
  ChannelSettings,
  DisconnectModal,
  NotificationGroup,
  NotificationGroups,
  SlackAuthentication,
  UserSelection,
} from 'components/organisms/module-settings/integrations/integrations-list';
import { IntegrationStatusTypes } from 'components/organisms/module-settings/integrations/integrations-list/authentication-types';
import { SectionLayout } from 'components/organisms/module-settings/integrations/integrations-list/slack/common';
import { NotificationsByGroup } from 'components/organisms/module-settings/integrations/integrations-list/slack/notification-group/constants';
import {
  AlertButton,
  Message,
} from 'components/organisms/module-settings/integrations/integrations-list/styles';
import { IntegrationPageTemplate } from 'components/templates';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { SlackIntegrationPageProps } from './slack-types';
import { PageContainer } from './slack.styles';
import { useSlackActions } from './use-slack-actions';

const emptyFunction = () => {};

const Wrapper = styled.div`
  margin-top: var(--spacing-07);
`;

export const SlackIntegrationPage = ({
  config,
  authMeta,
  disableBreadcrumb,
}: SlackIntegrationPageProps) => {
  const {
    slackLocalConfig,
    handleDisconnect,
    isDisconnecting,
    setShowDisconnectModal,
    showDisconnectModal,
    channelsList,
    loadingChannelsList,
    usersList,
    loadingUsersList,
    handleSettingsChange,
    slackNotificationsSettings,
    handleNotificationSettingsChange,
  } = useSlackActions(config);

  const translate = useTranslate();

  const alertMessage = useTranslation('integration.alert.error.message');
  const reAuthMessage = useTranslation(
    'settings.integrations.slack.settings.alert.button.message',
  );
  const reAuthenticate = useTranslation(
    'settings.integration.alert.button.message',
  );

  return (
    <IntegrationPageTemplate
      title={translate('settings.integrations.slack.title')}
      icon={<SlackLogo />}
      subText={translate('settings.integrations.slack.description_mini')}
      learnMoreLink="https://help.rocketlane.com/support/solutions/articles/67000696056-how-to-get-started-with-rocketlane-s-slack-integration-feature-"
      disableBreadcrumb={disableBreadcrumb}
    >
      <PageContainer>
        {config.integrationStatus === IntegrationStatusTypes.Expired && (
          <InlineNotification
            msg={
              <Message>
                <strong>{alertMessage}</strong> {reAuthMessage}
              </Message>
            }
            type={InlineNotificationType.Error}
            button={
              <AlertButton
                size="small"
                type="tertiary"
                href={authMeta?.oauthReauthUrl}
              >
                {reAuthenticate}
              </AlertButton>
            }
            icon={<Information20 />}
          />
        )}
        <SlackAuthentication
          handleDisconnect={() => setShowDisconnectModal(true)}
          authenticatedBy={config.createdBy}
          isDisconnecting={isDisconnecting}
          oauthUrl={authMeta?.oauthUrl}
          isConnected
        />
        <MaskableContent
          showMask={config.integrationStatus === IntegrationStatusTypes.Expired}
        >
          <Wrapper>
            <ChannelSettings
              settings={slackLocalConfig.settingsMeta}
              handleChange={handleSettingsChange}
              channelsList={channelsList}
              loading={loadingChannelsList}
            />
          </Wrapper>
          <Wrapper>
            <UserSelection
              settings={slackLocalConfig.settingsMeta}
              handleChange={handleSettingsChange}
              usersList={usersList}
              loading={loadingUsersList}
            />
          </Wrapper>
          <Wrapper>
            <SectionLayout
              title={
                <FormattedMessage id="settings.integrations.slack.notifications.title" />
              }
            >
              <NotificationGroup
                type={NotificationGroups.Task}
                notifications={slackNotificationsSettings}
                label={translate(
                  'settings.integrations.slack.notifications.title.task',
                )}
                icon={<ListChecked16 />}
                onNotificationChange={handleNotificationSettingsChange}
                dataCy="integrations.slack.task_checkbox"
              />
              <NotificationGroup
                type={NotificationGroups.Message}
                notifications={slackNotificationsSettings}
                label={translate(
                  'settings.integrations.slack.notifications.title.message',
                )}
                icon={<Chat16 />}
                onNotificationChange={handleNotificationSettingsChange}
                dataCy="integrations.slack.task_checkbox"
              />
              <>
                <NotificationGroup
                  type={NotificationGroups.Project}
                  notifications={slackNotificationsSettings}
                  label={translate(
                    'settings.integrations.slack.notifications.title.project',
                  )}
                  icon={<Task16 />}
                  onNotificationChange={handleNotificationSettingsChange}
                  dataCy="integrations.slack.project_checkbox"
                />
                <NotificationGroup
                  type={NotificationGroups.Phase}
                  notifications={slackNotificationsSettings}
                  label={translate(
                    'settings.integrations.slack.notifications.title.phase',
                  )}
                  icon={<Flag16 />}
                  onNotificationChange={handleNotificationSettingsChange}
                  dataCy="integrations.slack.phase_checkbox"
                />
                <NotificationGroup
                  type={NotificationGroups.Forms}
                  label={translate(
                    'settings.integrations.slack.notifications.title.form',
                  )}
                  notifications={slackNotificationsSettings}
                  icon={<AlignBoxMiddleCenter16 />}
                  onNotificationChange={handleNotificationSettingsChange}
                  dataCy="integrations.slack.project_checkbox"
                />
                <NotificationGroup
                  type={NotificationGroups.Space}
                  notifications={slackNotificationsSettings}
                  label={translate(
                    'settings.integrations.slack.notifications.title.spaces',
                  )}
                  icon={<Carbon16 />}
                  onNotificationChange={handleNotificationSettingsChange}
                  dataCy="integrations.slack.spaces_checkbox"
                />
                <NotificationGroup
                  type={NotificationGroups.Remainders}
                  notifications={slackNotificationsSettings}
                  label={translate(
                    'settings.integrations.slack.notifications.title.reminders',
                  )}
                  icon={<EventSchedule16 />}
                  onNotificationChange={handleNotificationSettingsChange}
                  timeConfigurable={
                    NotificationsByGroup[NotificationGroups.Remainders]
                  }
                  dataCy="integrations.slack.reminders_checkbox"
                />
                <NotificationGroup
                  type={NotificationGroups.TasksDigest}
                  notifications={slackNotificationsSettings}
                  label={translate(
                    'settings.integrations.slack.notifications.title.tasks_digest',
                  )}
                  icon={<TaskSettings16 />}
                  onNotificationChange={handleNotificationSettingsChange}
                  dataCy="integrations.slack.tasks_digest_checkbox"
                />
                <NotificationGroup
                  type={NotificationGroups.Approvals}
                  notifications={slackNotificationsSettings}
                  label={translate(
                    'settings.integrations.slack.notifications.title.approvals',
                  )}
                  icon={<Approved16 />}
                  onNotificationChange={handleNotificationSettingsChange}
                  dataCy="integrations.slack.approvals_checkbox"
                />
              </>
            </SectionLayout>
          </Wrapper>
        </MaskableContent>
      </PageContainer>

      {showDisconnectModal && handleDisconnect ? (
        <DisconnectModal
          onClose={() => setShowDisconnectModal(false)}
          onOkay={handleDisconnect}
          loading={isDisconnecting}
        />
      ) : null}
    </IntegrationPageTemplate>
  );
};

export const SlackIntegrationDisconnectedPage = ({
  authMeta,
  disableBreadcrumb,
}: AtLeast<SlackIntegrationPageProps, 'authMeta'>) => {
  const pageTitle = useTranslation('settings.integrations.slack.title');

  return (
    <IntegrationPageTemplate
      title={pageTitle}
      icon={<SlackLogo />}
      disableBreadcrumb={disableBreadcrumb}
    >
      <PageContainer>
        <SlackAuthentication
          oauthUrl={authMeta?.oauthUrl}
          handleDisconnect={emptyFunction}
        />
        <MaskableContent showMask>
          <Wrapper>
            <ChannelSettings />
          </Wrapper>
          <Wrapper>
            <UserSelection />
          </Wrapper>
        </MaskableContent>
      </PageContainer>
    </IntegrationPageTemplate>
  );
};
